import type { PayoutBatch } from 'api';

import { BATCHES_STATUS } from 'models/batches';

import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { TRANSLATIONS } from './constants';
import { formatApprovalNames } from './util';

type Props = {
  batchDetails: PayoutBatch | undefined;
  userId: number;
  remainingApprovals: number;
  isBatchApprovalRequired: boolean;
  canApprove: boolean;
  onOpenDeclineDialog: () => void;
  onApprove: () => void;
};

export const BatchApprovalSection = ({
  batchDetails,
  userId,
  remainingApprovals,
  isBatchApprovalRequired,
  canApprove,
  onOpenDeclineDialog,
  onApprove
}: Props) => {
  if (!batchDetails || !isBatchApprovalRequired) {
    return null;
  }

  const hasRejections = Boolean(batchDetails.rejections?.length);
  const hasApprovals = Boolean(batchDetails.approvals?.length);

  // --- declined ---
  if (hasRejections) {
    return (
      <div className="flex flex-col gap-3 p-6">
        <span className="mr-1 font-medium">
          {TRANSLATIONS.DECLINED_BY_LABEL}
        </span>

        {batchDetails.rejections?.map((rejection) => (
          <div
            key={rejection.user_id}
            className="flex items-center justify-between gap-3 py-3 first-of-type:p-0"
          >
            <div
              className="font-medium"
              data-testid="batch-details-declined-by"
            >
              {rejection.user_id === userId
                ? TRANSLATIONS.DECLINED_BY_YOU_LABEL
                : rejection.user_name}
            </div>
            <div className="text-right">
              <FormattedDateTime value={rejection.created_at} />
            </div>
          </div>
        ))}

        <div className="py-2 text-sm">
          {TRANSLATIONS.DECLINED_VIEW_ONLY_MESSAGE}
        </div>
      </div>
    );
  }

  // --- not declined ---
  if (!batchDetails.approvals) {
    return null;
  }

  return (
    <div className="flex flex-col p-6">
      {hasApprovals && (
        <div className="flex flex-col" data-testid="batch-details-approved-by">
          <span className="mr-1 font-medium">
            {TRANSLATIONS.APPROVED_BY_LABEL}
          </span>
          {formatApprovalNames(batchDetails.approvals, userId)}
        </div>
      )}

      {remainingApprovals !== 0 &&
        batchDetails.status == BATCHES_STATUS.WAITING_REVIEW && (
          <div
            className="rounded-2xl py-2 text-sm"
            data-testid="batch-details-needs-approval-by"
          >
            {TRANSLATIONS.NEEDS_APPROVAL_MESSAGE(remainingApprovals)}
          </div>
        )}

      {canApprove && (
        <div className="flex space-x-2">
          <SecondaryButton
            onClick={onOpenDeclineDialog}
            data-testid="batch-details-button-decline"
            danger
            className="flex-1"
          >
            {TRANSLATIONS.DECLINE_BUTTON_LABEL}
          </SecondaryButton>

          <SecondaryButton
            onClick={onApprove}
            data-testid="batch-details-button-approve"
            className="flex-1"
          >
            {TRANSLATIONS.APPROVE_BUTTON_LABEL}
          </SecondaryButton>
        </div>
      )}
    </div>
  );
};
